import type { InventoryStockLocationRecord } from '@/renderer/types/inventory';
import { Button, DropdownSelect } from '@components/common';
import type { DropdownSelectOption } from '@components/common/DropdownSelect';
import { Download } from 'lucide-react';

interface StockBalancesToolbarProps {
  stockLocationFilter: string;
  itemFilterIds: string[];
  locations: InventoryStockLocationRecord[];
  itemOptions: DropdownSelectOption[];
  itemOptionsLoading?: boolean;
  itemOptionsHasMore?: boolean;
  isExporting?: boolean;
  canExport?: boolean;
  groupByLocation?: boolean;
  onStockLocationChange: (value: string) => void;
  onItemFilterChange: (value: string[]) => void;
  onItemSearchChange: (value: string) => void;
  onLoadMoreItems: () => void;
  onExportCsv?: () => void;
  onCreate: () => void;
  onGroupByLocationChange?: (value: boolean) => void;
}

export function StockBalancesToolbar({
  stockLocationFilter,
  itemFilterIds,
  locations,
  itemOptions,
  itemOptionsLoading = false,
  itemOptionsHasMore = false,
  isExporting = false,
  canExport = false,
  groupByLocation = false,
  onStockLocationChange,
  onItemFilterChange,
  onItemSearchChange,
  onLoadMoreItems,
  onExportCsv,
  onCreate,
  onGroupByLocationChange,
}: StockBalancesToolbarProps) {
  const itemDisplayLabel = itemFilterIds.length
    ? `${itemFilterIds.length} item${itemFilterIds.length === 1 ? '' : 's'} selected`
    : undefined;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Stock balances
          </h3>
          <p className="mt-1 text-sm text-gray-500 max-w-[80%]">
            Capture opening and current stock quantities by item and location so
            outlet-level stock value is visible before automated movements are
            introduced.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          {onExportCsv ? (
            <Button
              variant="outline"
              onClick={onExportCsv}
              isLoading={isExporting}
              disabled={!canExport}
              className="min-w-[140px]"
            >
              {!isExporting && <Download size={16} />}
              Export CSV
            </Button>
          ) : null}
          <Button className="min-w-[150px]" onClick={onCreate}>
            Set balance
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <DropdownSelect
          label="Item"
          values={itemFilterIds}
          options={itemOptions}
          placeholder="All inventory items"
          customDisplayLabel={itemDisplayLabel}
          multiple
          searchable
          searchPlaceholder="Search items"
          onSearchChange={onItemSearchChange}
          hasMoreOptions={itemOptionsHasMore}
          isLoadingOptions={itemOptionsLoading}
          onLoadMoreOptions={onLoadMoreItems}
          emptyStateLabel="No items found."
          onMultiChange={onItemFilterChange}
        />
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <DropdownSelect
              label="Location"
              value={stockLocationFilter}
              options={[
                { value: 'all', label: 'All locations' },
                ...locations.map((location) => ({
                  value: location.id,
                  label: location.name,
                })),
              ]}
              onChange={onStockLocationChange}
            />
          </div>
          {onGroupByLocationChange ? (
            <label className="flex h-10 items-center gap-2 whitespace-nowrap rounded-lg border border-gray-200 px-3 text-sm text-gray-700">
              <input
                type="checkbox"
                aria-label="Group by location"
                checked={groupByLocation}
                disabled={stockLocationFilter !== 'all'}
                onChange={(event) =>
                  onGroupByLocationChange(event.target.checked)
                }
              />
              Group by location
            </label>
          ) : null}
        </div>
      </div>
    </div>
  );
}
